import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPriceLocal as formatPrice, fixAssetUrl } from "../lib/api";
import { useCart } from "../lib/cart";
import { Gamepad2, Filter, X, ShoppingCart, Package, Download, Star, SlidersHorizontal, ChevronDown } from "lucide-react";
import SEO from "../components/SEO";
import { toast } from "sonner";

const CONDITION_LABEL = {
  new: "Nové",
  very_good: "Velmi dobrý",
  good: "Dobrý",
  acceptable: "Přijatelný",
};

const TYPE_LABEL = { digital: "Digitální", physical: "Fyzické" };

export default function Gaming() {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [sel, setSel] = useState({
    genres: [],
    platforms: [],
    publishers: [],
    conditions: [],
    product_types: [],
  });
  const [mobileFilters, setMobileFilters] = useState(false);

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data));
    api.get("/gaming/filters").then((r) => setFilters(r.data)).catch(() => setFilters({}));
  }, []);

  useEffect(() => {
    const params = { sort };
    if (sel.genres.length) params.genre = sel.genres.join(",");
    if (sel.platforms.length) params.platform = sel.platforms.join(",");
    if (sel.publishers.length) params.publisher = sel.publishers.join(",");
    if (sel.conditions.length) params.condition = sel.conditions.join(",");
    if (sel.product_types.length) params.product_type = sel.product_types.join(",");
    setLoading(true);
    api.get("/gaming/products", { params })
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [sel, sort]);

  const toggle = (key, value) => {
    setSel((prev) => {
      const cur = prev[key] || [];
      return { ...prev, [key]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  };
  const clearAll = () => setSel({ genres: [], platforms: [], publishers: [], conditions: [], product_types: [] });
  const totalActive = Object.values(sel).reduce((s, arr) => s + arr.length, 0);

  const hero = settings ? {
    title: settings.gaming_hero_title || "Gaming shop",
    subtitle: settings.gaming_hero_subtitle || "",
    description: settings.gaming_hero_description || "",
    image: settings.gaming_hero_image || "",
    logo: settings.gaming_logo_url || "",
  } : null;

  return (
    <div className="pb-24" data-testid="gaming-page">
      <SEO title={hero?.title || "Gaming"} description={hero?.subtitle || "Herní shop – nové a bazarové hry, konzole a příslušenství."}/>

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-purple-950 via-indigo-950 to-zinc-950 text-white overflow-hidden">
        {hero?.image && (
          <>
            <img src={fixAssetUrl(hero.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" onError={(e) => e.currentTarget.remove()}/>
            <div className="absolute inset-0 bg-gradient-to-br from-purple-950/70 via-indigo-950/60 to-zinc-950/80"/>
          </>
        )}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="flex items-center gap-3 mb-4">
            {hero?.logo ? (
              <img src={fixAssetUrl(hero.logo)} alt="" className="h-12 sm:h-14 max-w-[220px] object-contain" data-testid="gaming-logo"/>
            ) : (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-500/20 border border-purple-400/40 text-xs uppercase tracking-widest font-bold text-purple-200">
                <Gamepad2 className="w-3.5 h-3.5"/> Gaming
              </div>
            )}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="gaming-hero-title">{hero?.title}</h1>
          {hero?.subtitle && <p className="text-lg sm:text-xl text-zinc-200 mt-4 max-w-2xl">{hero.subtitle}</p>}
          {hero?.description && <p className="text-sm sm:text-base text-zinc-300/90 mt-4 max-w-3xl leading-relaxed whitespace-pre-line">{hero.description}</p>}
        </div>
      </section>

      {/* CONTENT: sidebar + grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Mobile filter toggle */}
        <button onClick={() => setMobileFilters(true)} className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-zinc-200 bg-white text-sm font-semibold shadow-sm" data-testid="gaming-mobile-filters-open">
          <SlidersHorizontal className="w-4 h-4"/> Filtry {totalActive > 0 && <span className="bg-purple-600 text-white rounded-full px-1.5 text-xs">{totalActive}</span>}
        </button>

        {/* Desktop sidebar */}
        <aside className={`lg:sticky lg:top-24 lg:self-start ${mobileFilters ? "fixed inset-0 z-50 bg-white overflow-y-auto p-6" : "hidden lg:block"}`} data-testid="gaming-filters">
          <div className="flex items-center justify-between mb-5">
            <div className="font-display font-bold text-lg inline-flex items-center gap-2"><Filter className="w-4 h-4"/> Filtry</div>
            <div className="flex items-center gap-2">
              {totalActive > 0 && (
                <button onClick={clearAll} className="text-xs text-zinc-500 hover:text-zinc-900 underline" data-testid="gaming-filters-clear">Vyčistit ({totalActive})</button>
              )}
              <button onClick={() => setMobileFilters(false)} className="lg:hidden p-1"><X className="w-5 h-5"/></button>
            </div>
          </div>

          <FilterGroup title="Platforma" items={filters?.platforms} selected={sel.platforms} onToggle={(v) => toggle("platforms", v)} testid="platforms"/>
          <FilterGroup title="Žánr" items={filters?.genres} selected={sel.genres} onToggle={(v) => toggle("genres", v)} testid="genres"/>
          <FilterGroup title="Vydavatel" items={filters?.publishers} selected={sel.publishers} onToggle={(v) => toggle("publishers", v)} testid="publishers"/>
          <FilterGroup title="Stav" items={(filters?.conditions || []).map((c) => ({ ...c, label: CONDITION_LABEL[c.value] || c.value }))} selected={sel.conditions} onToggle={(v) => toggle("conditions", v)} testid="conditions"/>
          <FilterGroup title="Typ produktu" items={(filters?.product_types || []).map((c) => ({ ...c, label: TYPE_LABEL[c.value] || c.value }))} selected={sel.product_types} onToggle={(v) => toggle("product_types", v)} testid="types"/>
        </aside>

        {/* Grid */}
        <div>
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-5">
            <div className="text-sm text-zinc-600">
              <span className="font-semibold text-zinc-900">{products.length}</span> {products.length === 1 ? "produkt" : products.length >= 2 && products.length <= 4 ? "produkty" : "produktů"}
            </div>
            <label className="relative inline-flex items-center gap-2 text-sm">
              <span className="text-zinc-500">Řadit</span>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className="pr-8 pl-3 py-1.5 rounded-full border border-zinc-200 bg-white text-sm font-medium appearance-none focus:outline-none focus:ring-2 focus:ring-purple-500/20" data-testid="gaming-sort">
                <option value="newest">Nejnovější</option>
                <option value="price_asc">Cena vzestupně</option>
                <option value="price_desc">Cena sestupně</option>
                <option value="featured">Doporučené</option>
              </select>
              <ChevronDown className="w-4 h-4 text-zinc-500 absolute right-2 pointer-events-none"/>
            </label>
          </div>

          {loading ? (
            <div className="text-center py-16 text-zinc-500">Načítání…</div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 rounded-3xl bg-zinc-50 border border-dashed border-zinc-200">
              <Gamepad2 className="w-12 h-12 mx-auto text-zinc-300 mb-3"/>
              <div className="font-semibold">Zatím žádné produkty</div>
              <div className="text-sm text-zinc-500 mt-1">Zkuste jiné filtry, nebo se vraťte později.</div>
              {totalActive > 0 && (
                <button onClick={clearAll} className="mt-4 text-sm underline text-purple-700">Vyčistit filtry</button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gaming-grid">
              {products.map((p) => <GamingCard key={p.id} product={p}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ title, items, selected, onToggle, testid }) {
  const [open, setOpen] = useState(true);
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4 border-b border-zinc-100 pb-4">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-left font-semibold text-sm mb-2" data-testid={`gaming-filter-${testid}-toggle`}>
        <span>{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="space-y-1.5 mt-2">
          {items.map((it) => {
            const value = it.value ?? it;
            const count = it.count;
            const label = it.label || value;
            const checked = selected.includes(value);
            return (
              <label key={value} className="flex items-center gap-2 cursor-pointer text-sm hover:text-zinc-900 group">
                <input type="checkbox" checked={checked} onChange={() => onToggle(value)} className="w-4 h-4 accent-purple-600" data-testid={`gaming-filter-${testid}-${value}`}/>
                <span className="flex-1">{label}</span>
                {typeof count === "number" && <span className="text-xs text-zinc-400 group-hover:text-zinc-500">{count}</span>}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GamingCard({ product: p }) {
  const { add } = useCart();
  const image = p.images?.[0];
  const inStock = (p.stock_qty ?? 0) > 0;
  const onSale = p.price_original && p.price_original > p.price;
  const addNow = (e) => {
    e.preventDefault();
    if (!inStock) return toast.error("Skladem 0 ks");
    add({ ...p, kind: "gaming" });
    toast.success(`${p.name} přidáno do košíku`);
  };

  return (
    <Link to={`/gaming/${p.slug}`} className="product-card group relative overflow-hidden rounded-3xl bg-white flex flex-col" data-testid={`gaming-card-${p.slug}`}>
      <div className="aspect-[4/3] bg-gradient-to-br from-zinc-100 via-white to-purple-50 relative overflow-hidden">
        {image ? (
          <img src={fixAssetUrl(image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => e.currentTarget.style.display = "none"}/>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-300"><Gamepad2 className="w-14 h-14"/></div>
        )}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
          {p.condition && p.condition !== "new" && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-amber-500 text-white shadow-sm">Bazar · {CONDITION_LABEL[p.condition]}</span>
          )}
          {p.condition === "new" && p.labels?.includes("new") && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-emerald-500 text-white shadow-sm">Novinka</span>
          )}
          {onSale && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-rose-500 text-white shadow-sm">Sleva</span>
          )}
        </div>
        <div className="absolute top-2.5 right-2.5">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-white/90 backdrop-blur text-zinc-800 shadow-sm">
            {p.product_type === "digital" ? <><Download className="w-3 h-3"/> Digital</> : <><Package className="w-3 h-3"/> Fyzické</>}
          </span>
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        {p.publisher && <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{p.publisher}</div>}
        <div className="font-display font-bold text-base leading-tight mt-1 line-clamp-2 group-hover:text-purple-700 transition-colors">{p.name}</div>
        {p.platforms?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {p.platforms.slice(0, 3).map((pl) => (
              <span key={pl} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 uppercase tracking-wide">{pl}</span>
            ))}
          </div>
        )}
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div>
            {onSale && <div className="text-xs text-zinc-400 line-through leading-none">{formatPrice(p.price_original)}</div>}
            <div className={`font-display font-bold text-xl leading-tight ${onSale ? "text-rose-600" : ""}`}>{formatPrice(p.price)}</div>
            <div className={`text-[11px] font-semibold mt-0.5 ${inStock ? "text-emerald-600" : "text-rose-500"}`}>
              {inStock ? `Skladem: ${p.stock_qty} ks` : "Vyprodáno"}
            </div>
          </div>
          <button onClick={addNow} disabled={!inStock} className="p-2.5 rounded-full bg-zinc-900 text-white hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" aria-label="Přidat do košíku" data-testid={`gaming-add-${p.slug}`}>
            <ShoppingCart className="w-4 h-4"/>
          </button>
        </div>
      </div>
    </Link>
  );
}
